import { useEffect } from 'react';
import { useBushingStore } from '@/stores/useBushingStore';
import { useInputStore } from '@/stores/useInputStore';

/**
 * Calculate the C1 H value.
 *
 * Formula ported from the legacy OptiBush implementation:
 *
 *   H = (core_length - sleeve_length) / 2
 *
 * The calculation is applicable only to C1 bushings.
 * The result is rounded to one decimal place.
 */
function computeC1H(
  coreLength: number | null,
  outerSleeveLength: number | null,
): number | null {
  if (coreLength == null || outerSleeveLength == null) {
    return null;
  }
  const calculatedH = (coreLength - outerSleeveLength) / 2;

  return Math.round(calculatedH * 10) / 10;
}

/**
 * useC1HDefault
 *
 * Reactively calculates and updates the C1 H value whenever:
 *
 * - coreLength changes
 * - outerSleeveLength changes
 * - bushingType changes
 * - an SOR upload updates coreLength or sleeveLength in useInputStore
 */
export function useC1HDefault(): void {
  const bushingType = useBushingStore((state) => state.bushingType);

  const coreLength = useInputStore(
    (state) => state.packaging.coreLength,
  );

  const outerSleeveLength = useInputStore(
    (state) => state.packaging.outerSleeveLength,
  );

  useEffect(() => {
    /*
     * H is applicable only to C1 bushings.
     *
     * Clear the existing value when the user changes from C1 to another
     * bushing type, preventing a stale C1 value from remaining in the store.
     */
    if (bushingType !== 'C1') {
      const currentH = useInputStore.getState().packaging.h;

      if (currentH != null) {
        useInputStore
          .getState()
          .setPackagingField('h', null);
      }

      return;
    }

    const calculatedH = computeC1H(
      coreLength,
      outerSleeveLength,
    );

    const currentH = useInputStore.getState().packaging.h;

    /*
     * Avoid unnecessary Zustand updates and component re-renders.
     *
     * Write null as well so that an old H value is cleared if coreLength
     * or sleeveLength becomes unavailable.
     */
    if (calculatedH !== currentH) {
      useInputStore
        .getState()
        .setPackagingField('h', calculatedH);
    }
  }, [bushingType, coreLength, outerSleeveLength]);
}